/**
 * New console — pull to refresh, on a phone.
 *
 * A phone shows no refresh buttons (`.nt-refresh` is hidden under 640px). Pulling the
 * page down from the very top reloads the screen instead, the way a mobile app does.
 *
 * A screen says what a refresh means with `usePullRefresh(fn, busy)`: `fn` reloads its
 * data and keeps its filters, and `busy` is its own loading flag, which is how the
 * spinner knows the reload has finished. A screen that registers nothing is remounted
 * — what a browser reload would have done, without reloading the whole app.
 *
 * While this is active the browser's own pull-to-reload is switched off, as the classic
 * does, because that one reloads the app and loses the user's place in it. The gesture
 * is left alone when it starts inside a dialog, the menu, a form field or anything that
 * is itself scrolled, and when the finger moves sideways first — the board tabs and the
 * wide tables scroll that way.
 */
import {
  Fragment, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';

import { usePhone } from './Primitives';
import { IconRefresh } from './Icons';

const PullContext = createContext(null);

const RESIST = 0.5;     // the indicator travels half as far as the finger
const TRIGGER = 72;     // indicator travel that refreshes on release
const MAX_PULL = 120;
const REST = 64;        // where the indicator waits while the screen reloads
const CHIP = 40;
const MIN_SPIN = 600;   // long enough to see that something happened
const MAX_SPIN = 20000; // a reload that never reports back must not spin forever

const SKIP = [
  '[role="dialog"]', '[aria-modal="true"]', '.nt-rail', '.nt-dock', '.nt-scrim', '.rs-picker-popup',
  'input', 'textarea', 'select', '[contenteditable="true"]'
].join(', ');

/** True when a pull starting on `target` belongs to something other than the page. */
const ownedElsewhere = (target) => {
  if (document.querySelector('[aria-modal="true"], .nt-app.navopen')) return true;
  for (let el = target; el && el.nodeType === 1 && el !== document.body; el = el.parentElement) {
    if (el.matches(SKIP)) return true;
    if (el.scrollTop > 0) return true;
  }
  return false;
};

/**
 * Registers what a pull does on this screen. `fn` is read when the pull happens, so it
 * always sees the screen's current filters; `busy` keeps the spinner up until the
 * reload it started has finished.
 */
export const usePullRefresh = (fn, busy = false) => {
  const ctx = useContext(PullContext);
  const entry = useRef({ run: fn, busy: !!busy });
  entry.current.run = fn;
  entry.current.busy = !!busy;
  useEffect(() => (ctx ? ctx.register(entry) : undefined), [ctx]);
};

export const PullToRefresh = ({ children }) => {
  const phone = usePhone();
  const entries = useRef(new Set());
  const chip = useRef(null);
  const active = useRef(false);
  const timer = useRef(0);
  const [epoch, setEpoch] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const api = useMemo(() => ({
    register: (entry) => {
      entries.current.add(entry);
      return () => { entries.current.delete(entry); };
    }
  }), []);

  /* Moved by hand rather than through state: touchmove fires on every frame. */
  const place = useCallback((travel, animate) => {
    const el = chip.current;
    if (!el) return;
    el.style.transition = animate ? 'transform .22s ease, opacity .22s ease' : 'none';
    el.style.transform = `translate(-50%, ${Math.round(travel - CHIP - 4)}px)`;
    el.style.opacity = travel > 2 ? String(Math.min(1, travel / TRIGGER)) : '0';
    el.classList.toggle('ready', travel >= TRIGGER);
    const icon = el.querySelector('svg');
    if (icon) icon.style.transform = active.current ? '' : `rotate(${Math.round(travel * 4)}deg)`;
  }, []);

  const finish = useCallback(() => {
    window.clearInterval(timer.current);
    timer.current = 0;
    active.current = false;
    setRefreshing(false);
    place(0, true);
  }, [place]);

  const refresh = useCallback(() => {
    active.current = true;
    setRefreshing(true);
    place(REST, true);
    const list = Array.from(entries.current);
    if (list.length) {
      list.forEach((entry) => {
        try {
          if (typeof entry.current.run === 'function') entry.current.run();
        } catch (err) {
          /* One screen's reload throwing must not leave the spinner up. */
        }
      });
    } else {
      setEpoch((n) => n + 1);
    }
    const started = Date.now();
    timer.current = window.setInterval(() => {
      const elapsed = Date.now() - started;
      const busy = Array.from(entries.current).some((entry) => entry.current.busy);
      if ((elapsed >= MIN_SPIN && !busy) || elapsed >= MAX_SPIN) finish();
    }, 120);
  }, [place, finish]);

  useEffect(() => () => window.clearInterval(timer.current), []);

  useEffect(() => {
    if (!phone) return undefined;
    const root = document.documentElement;
    const before = root.style.overscrollBehaviorY;
    root.style.overscrollBehaviorY = 'none';

    let start = null;
    let pulling = false;
    let travel = 0;
    const atTop = () => (window.scrollY || root.scrollTop || 0) <= 0;

    const onStart = (e) => {
      start = null;
      pulling = false;
      travel = 0;
      if (active.current || e.touches.length !== 1 || !atTop() || ownedElsewhere(e.target)) return;
      start = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      /* Slide out from under the topbar, whatever height it has wrapped to. */
      const bar = document.querySelector('.nt-top');
      if (chip.current) {
        chip.current.style.top = `${bar ? Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) : 0}px`;
      }
    };

    const onMove = (e) => {
      if (!start) return;
      const t = e.touches[0];
      if (!pulling) {
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || !atTop()) {
          start = null;
          return;
        }
        pulling = true;
        /* Measured from here, so the indicator starts at the edge rather than jumping. */
        start = { x: t.clientX, y: t.clientY };
      }
      if (e.cancelable) e.preventDefault();
      travel = Math.min(MAX_PULL, Math.max(0, t.clientY - start.y) * RESIST);
      place(travel, false);
    };

    const onEnd = () => {
      if (!start) return;
      const wasPulling = pulling;
      start = null;
      pulling = false;
      if (!wasPulling) return;
      if (travel >= TRIGGER) refresh(); else place(0, true);
      travel = 0;
    };

    const onCancel = () => {
      if (pulling) place(0, true);
      start = null;
      pulling = false;
      travel = 0;
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onCancel, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onCancel);
      root.style.overscrollBehaviorY = before;
    };
  }, [phone, place, refresh]);

  return (
    <PullContext.Provider value={api}>
      <Fragment key={epoch}>{children}</Fragment>
      {phone ? (
        <div
          ref={chip}
          className={`nt-ptr${refreshing ? ' spin' : ''}`}
          role="status"
          aria-live="polite"
          aria-label={refreshing ? 'Refreshing' : undefined}
        >
          <IconRefresh />
        </div>
      ) : null}
    </PullContext.Provider>
  );
};
